import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { EDS, priceText, resolvePrice } from "../theme";
import { firstMediaUrl } from "lib/media";

export default function ProductCard({ product, currency, onOpen, onAdd, canOrder }) {
  const image = firstMediaUrl(product?.images);
  const unitPrice = resolvePrice(product);
  const hasPrice = Number(unitPrice) > 0;

  return (
    <Box
      onClick={onOpen}
      sx={{
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "opacity 200ms ease",
        "&:hover": { opacity: 0.92 },
        "&:hover .eds-product-image": { transform: "scale(1.02)" },
      }}
    >
      <Box
        sx={{
          aspectRatio: "1 / 1",
          bgcolor: EDS.imageBg,
          borderRadius: `${EDS.radius}px`,
          overflow: "hidden",
        }}
      >
        {image ? (
          <Box
            className="eds-product-image"
            component="img"
            src={image}
            alt={product.name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 500ms ease",
            }}
          />
        ) : null}
      </Box>

      <Box sx={{ pt: 1.5, flex: 1, display: "flex", flexDirection: "column" }}>
        <Box
          component="span"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 400,
            color: EDS.subtle,
          }}
        >
          {product.sku || "SKU pending"}
        </Box>
        <Box
          component="h3"
          sx={{
            m: "0.3rem 0 0",
            fontSize: "0.9375rem",
            fontWeight: 500,
            lineHeight: 1.45,
            color: EDS.ink,
            letterSpacing: "-0.02em",
          }}
        >
          {product.name}
        </Box>

        <Box
          sx={{
            mt: "auto",
            pt: 1,
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 500,
              color: EDS.ink,
              letterSpacing: "-0.02em",
            }}
          >
            {priceText(unitPrice, currency)}
          </Box>
          {hasPrice && canOrder && onAdd ? (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAdd(product);
              }}
              sx={{
                margin: 0,
                border: 0,
                padding: 0,
                background: "none",
                cursor: "pointer",
                color: EDS.link,
                fontSize: "0.8125rem",
                fontWeight: 500,
                "&:hover": { color: EDS.linkHover },
              }}
            >
              Add
            </Box>
          ) : (
            <Box
              component="span"
              sx={{ fontSize: "0.8125rem", fontWeight: 400, color: EDS.subtle }}
            >
              View
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string,
    sku: PropTypes.string,
    images: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  }).isRequired,
  currency: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  canOrder: PropTypes.bool,
};

ProductCard.defaultProps = {
  currency: "USD",
  onAdd: null,
  canOrder: false,
};
